import { LayoutDashboard, MapPin, Package, Users, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { activeModules } from '@/app/modules';
import { I18N_NAMESPACES } from '@/shared/i18n';
import { cn } from '@/shared/lib/cn';

interface NavItem {
  to: string;
  label: string;
  Icon?: LucideIcon;
}

interface NavGroup {
  headline?: string;
  dotClass?: string;
  borderClass?: string;
  hoverClass?: string;
  items: NavItem[];
}

interface SidebarNavProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

const linkClass = (
  { isActive }: { isActive: boolean },
  collapsed: boolean,
  borderClass = 'border-r-primary',
  hoverClass = 'hover:bg-primary-tint',
) =>
  cn(
    'no-underline text-sm rounded-xs p-2 transition-colors flex items-center overflow-hidden',
    collapsed ? 'justify-center' : 'gap-2',
    hoverClass,
    'hover:shadow-sm',
    isActive ? cn('bg-white border-r-3 shadow-sm', borderClass) : '',
  );

export const SidebarNav = ({ collapsed, onNavigate }: SidebarNavProps) => {
  const hasRole = useAuthStore((s) => s.hasRole);
  const { t } = useTranslation(I18N_NAMESPACES);

  const moduleNav = (id: string): NavItem[] => {
    const module = activeModules.find((m) => m.id === id);

    return (module?.navigation ?? [])
      .filter((item) => !item.roles || item.roles.some((r) => hasRole(r)))
      .map((item) => ({
        to: item.path,
        label: t(item.labelKey),
        ...(item.icon ? { Icon: item.icon } : {}),
      }));
  };

  const groups: NavGroup[] = [
    {
      headline: t('app:sidebar.groups.general'),
      dotClass: 'bg-navy',
      borderClass: 'border-r-navy',
      hoverClass: 'hover:bg-navy/10',
      items: [
        { to: '/dashboard', label: t('app:nav.dashboard'), Icon: LayoutDashboard },
        ...(hasRole('monitor')
          ? []
          : [
              { to: '/devices', label: t('app:nav.devices'), Icon: Package },
              ...(__FEATURE_MAP__ ? [{ to: '/map', label: t('app:nav.map'), Icon: MapPin }] : []),
            ]),
      ],
    },
    {
      headline: t('app:sidebar.groups.alerts'),
      dotClass: 'bg-cerulean',
      borderClass: 'border-r-cerulean',
      hoverClass: 'hover:bg-cerulean/10',
      items: [...moduleNav('alert'), ...moduleNav('notifications')],
    },
    {
      headline: t('app:sidebar.groups.elabel'),
      dotClass: 'bg-pine',
      borderClass: 'border-r-pine',
      hoverClass: 'hover:bg-pine/10',
      items: moduleNav('elabel'),
    },
    ...(hasRole('admin') || hasRole('manager')
      ? [
          {
            headline: t('app:sidebar.groups.users'),
            dotClass: 'bg-sky',
            borderClass: 'border-r-sky',
            hoverClass: 'hover:bg-sky/10',
            items: [{ to: '/admin/users', label: t('app:nav.userManagement'), Icon: Users }],
          },
        ]
      : []),
    { items: moduleNav('native') },
  ].filter((group) => group.items.length > 0);

  return (
    <nav aria-label={t('app:nav.primaryAria')} className="grid gap-4">
      {groups.map((group, index) => (
        <div className="grid gap-1" key={group.headline ?? `nav-group-${index}`}>
          {group.headline ? (
            <span
              className={cn(
                'flex items-center gap-1.5 overflow-hidden whitespace-nowrap px-2 text-[11px] font-semibold tracking-wider uppercase text-ink-muted',
                collapsed ? 'max-h-0 opacity-0' : 'max-h-6 opacity-100',
              )}
            >
              {group.dotClass ? (
                <span
                  aria-hidden
                  className={cn('size-1.5 shrink-0 rounded-full', group.dotClass)}
                />
              ) : null}
              {group.headline}
            </span>
          ) : null}
          {group.items.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              className={(state) =>
                linkClass(state, collapsed, group.borderClass, group.hoverClass)
              }
              onClick={onNavigate}
              title={collapsed ? label : undefined}
              to={to}
            >
              {Icon ? <Icon aria-hidden="true" className="size-4 shrink-0" /> : null}
              <span
                className={cn(
                  'overflow-hidden whitespace-nowrap',
                  collapsed ? 'max-w-0 opacity-0' : 'max-w-xs opacity-100',
                )}
              >
                {label}
              </span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
};
